export interface FormatPersonAnswerOptions {
  formerLabel?: string
  emptyLabel?: string
  loading?: boolean
}

/**
 * Pure helper for formatting PERSON form answers.
 * Resolves single uid or uid array to member display names.
 * Unknown uids resolve to formerLabel when loaded, or emptyLabel when loading.
 */
export function formatPersonAnswer(
  value: unknown,
  memberNames: Map<string, string> | Record<string, string> | undefined | null,
  options?: FormatPersonAnswerOptions,
): string {
  const emptyLabel = options?.emptyLabel ?? '—'
  const formerLabel = options?.formerLabel ?? 'Former member'
  const loading = Boolean(options?.loading)

  let uids: string[] = []
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed) uids = [trimmed]
  } else if (Array.isArray(value)) {
    uids = value
      .filter((entry): entry is string => typeof entry === 'string' && Boolean(entry.trim()))
      .map((entry) => entry.trim())
  }

  if (uids.length === 0) {
    return emptyLabel
  }

  const resolveUid = (uid: string): string => {
    if (!uid) return ''
    let name: string | undefined
    if (memberNames instanceof Map) {
      name = memberNames.get(uid)
    } else if (memberNames && typeof memberNames === 'object') {
      name = (memberNames as Record<string, string>)[uid]
    }
    if (name) return name
    if (loading) {
      return emptyLabel
    }
    return formerLabel
  }

  const resolved = uids.map(resolveUid).filter(Boolean)
  if (resolved.length === 0) {
    return emptyLabel
  }
  if (loading && resolved.every((r) => r === emptyLabel)) {
    return emptyLabel
  }
  return resolved.join(', ')
}
